import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { TaskBoardClosed, TaskBoardTask, TaskBoardTaskDetail } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { taskBoardApi } from "../../api/client.js";

/** The page only reads; the MCP tools write, so nothing here may be served from the app's 30 s cache. */
const FRESH = { staleTime: 0 } as const;

/** A task that was closed or deleted while someone had it open, as opposed to a server that is briefly away. */
export class TaskGoneError extends Error {}

const keys = {
  tasks: (s: AuthSession | null) => ["task-board", s?.realm, s?.sub, "tasks"] as const,
  task: (s: AuthSession | null, id: number | null) => ["task-board", s?.realm, s?.sub, "task", id] as const,
  closed: (s: AuthSession | null) => ["task-board", s?.realm, s?.sub, "closed"] as const,
};

/**
 * A refetch on window focus is how a change reaches the page. Query's own focus handling listens to `visibilitychange`
 * only, which never fires for a window that stays visible beside the terminal, so this listens to the real event.
 */
export function useTaskBoardFocusRefresh(session: AuthSession) {
  const client = useQueryClient();
  useEffect(() => {
    const refresh = () => void client.invalidateQueries({ queryKey: ["task-board", session.realm, session.sub] });
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [client, session.realm, session.sub]);
}

export function useTaskBoardTasks(session: AuthSession | null) {
  return useQuery<TaskBoardTask[], Error>({
    queryKey: keys.tasks(session),
    enabled: session !== null,
    ...FRESH,
    queryFn: async () => {
      const res = await taskBoardApi.listTaskBoardTasks({ query: {} });
      if (res.status !== 200) throw new Error(`GET /task-board returned ${String(res.status)}`);
      return res.body.tasks;
    },
  });
}

export function useTaskBoardTask(session: AuthSession | null, id: number | null) {
  return useQuery<TaskBoardTaskDetail, Error>({
    queryKey: keys.task(session, id),
    enabled: session !== null && id !== null,
    ...FRESH,
    queryFn: async () => {
      const res = await taskBoardApi.getTaskBoardTask({ params: { id: id! } });
      if (res.status === 404) throw new TaskGoneError();
      if (res.status !== 200) throw new Error(`GET /task-board/tasks/${String(id)} returned ${String(res.status)}`);
      return res.body;
    },
  });
}

/** Fetched when someone opens the list. 500 rows is the route's page; `total` tells the page if there are more. */
export function useTaskBoardClosed(session: AuthSession | null, enabled: boolean) {
  return useQuery<{ closed: TaskBoardClosed[]; total: number }, Error>({
    queryKey: keys.closed(session),
    enabled: session !== null && enabled,
    ...FRESH,
    queryFn: async () => {
      const res = await taskBoardApi.listTaskBoardClosed({ query: { limit: 500, offset: 0 } });
      if (res.status !== 200) throw new Error(`GET /task-board/closed returned ${String(res.status)}`);
      return res.body;
    },
  });
}
